import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "@/lib/api";
import { Lock, Send, Loader2, ArrowLeft, ShieldCheck, RotateCw } from "lucide-react";

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return fallback;
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("id");
  const [identifier, setIdentifier] = useState("");
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => { api.authMe().then(() => navigate("/", { replace: true })).catch(() => {}); }, [navigate]);

  const requestOtp = async (e) => {
    e && e.preventDefault();
    if (!identifier.trim()) { setError("Inserisci email o username."); return; }
    setBusy(true); setError(""); setInfo("");
    try {
      const r = await api.authRequestOtp(identifier.trim());
      setChallenge(r.challenge); setCode(""); setStep("otp");
      if (step === "otp") setInfo("Nuovo codice richiesto. Il precedente non è più valido.");
    } catch (err) { setError(errMsg(err, "Si è verificato un errore. Riprova.")); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setError("Inserisci il codice a 6 cifre."); return; }
    setBusy(true); setError(""); setInfo("");
    try { await api.authVerifyOtp(challenge, code); navigate("/", { replace: true }); }
    catch (err) { setError(errMsg(err, "Codice non valido.")); setCode(""); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[hsl(var(--background))] px-4 py-10" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <img src="/agfit-logo.png" alt="AGFIT" className="h-14 w-14 rounded-xl object-cover ring-1 ring-[#d4af37]/40" data-testid="admin-login-logo" />
          <div>
            <div className="font-display text-lg font-extrabold leading-none" data-testid="admin-login-title">AGFIT</div>
            <div className="mt-1 text-[11px] text-muted-foreground">Area amministratore</div>
          </div>
        </div>

        <div className="panel p-6 sm:p-7">
          {step === "id" ? (
            <form onSubmit={requestOtp} className="space-y-5" data-testid="login-id-form">
              <div>
                <h1 className="flex items-center gap-2 font-display text-2xl font-bold"><Lock className="h-5 w-5 text-primary" /> Accesso riservato</h1>
                <p className="mt-2 text-sm text-muted-foreground">Riceverai un codice OTP su Telegram per completare l'accesso.</p>
              </div>
              <label className="block space-y-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Email o username amministratore</span>
                <input className="lab-input" type="text" autoComplete="username" autoFocus value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="nome@email.com" data-testid="login-identifier-input" />
              </label>
              {error && <div className="rounded-lg border border-[hsl(0_72%_60%)]/40 bg-[hsl(0_72%_60%)]/10 px-3 py-2 text-xs text-[hsl(0_72%_72%)]" data-testid="login-error">{error}</div>}
              <button type="submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="login-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Accedi
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-5" data-testid="login-otp-form">
              <div>
                <h1 className="flex items-center gap-2 font-display text-2xl font-bold"><ShieldCheck className="h-5 w-5 text-primary" /> Controlla Telegram</h1>
                <p className="mt-2 text-sm text-muted-foreground">Abbiamo inviato un codice di sicurezza al tuo Telegram.</p>
              </div>
              <label className="block space-y-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Inserisci codice OTP</span>
                <input className="lab-input text-center font-mono-num text-2xl tracking-[0.5em]" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" data-testid="login-otp-input" />
              </label>
              {error && <div className="rounded-lg border border-[hsl(0_72%_60%)]/40 bg-[hsl(0_72%_60%)]/10 px-3 py-2 text-xs text-[hsl(0_72%_72%)]" data-testid="login-error">{error}</div>}
              {info && <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary" data-testid="login-info">{info}</div>}
              <button type="submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="login-verify-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verifica e accedi
              </button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => { setStep("id"); setError(""); setInfo(""); }} className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground" data-testid="login-back-btn"><ArrowLeft className="h-3.5 w-3.5" /> Indietro</button>
                <button type="button" onClick={() => requestOtp()} disabled={busy} className="inline-flex items-center gap-1 font-semibold text-primary transition-colors hover:underline disabled:opacity-50" data-testid="login-resend-btn"><RotateCw className="h-3.5 w-3.5" /> Richiedi nuovo codice</button>
              </div>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-[11px] text-muted-foreground/70">Accesso protetto · sessione cifrata · codice monouso</p>
      </div>
    </div>
  );
}
